import React from 'react';
import { FileCode, AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { useScriptEditorStore } from '../../store/scriptEditorStore';
import { cn } from '../../utils/cn';

export const ScriptEditorHeader: React.FC = () => {
  const {
    activeTabId,
    files,
    diagnosticCounts,
    isCompiling,
    lastCompilationResult,
    setActiveBottomTab,
  } = useScriptEditorStore();

  const activeFile = activeTabId ? files[activeTabId] : null;

  const handleDiagnosticsClick = () => {
    setActiveBottomTab('problems');
  };

  return (
    <div className="flex items-center justify-between px-3 py-1 bg-zinc-950 border-t border-zinc-800 text-xs">
      {/* Left side - File info */}
      <div className="flex items-center gap-4">
        {activeFile ? (
          <>
            {/* File path */}
            <div className="flex items-center gap-1.5 text-zinc-400">
              <FileCode className="w-3.5 h-3.5" />
              <span className="truncate max-w-[300px]" title={activeFile.path}>
                {activeFile.path}
              </span>
            </div>

            {/* Cursor position */}
            <div className="text-zinc-500">
              Ln {activeFile.cursorPosition.line}, Col {activeFile.cursorPosition.column}
            </div>

            {/* Language */}
            <div className="text-zinc-500">
              {activeFile.language === 'typescript' ? 'TypeScript' : 'JavaScript'}
            </div>

            {/* Encoding */}
            <div className="text-zinc-500">
              UTF-8
            </div>
          </>
        ) : (
          <span className="text-zinc-500">No file open</span>
        )}
      </div>

      {/* Right side - Status info */}
      <div className="flex items-center gap-4">
        {/* Compilation status */}
        {isCompiling ? (
          <div className="flex items-center gap-1.5 text-blue-400">
            <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <span>Compiling...</span>
          </div>
        ) : lastCompilationResult ? (
          <div
            className={cn(
              'flex items-center gap-1.5',
              lastCompilationResult.success ? 'text-green-400' : 'text-red-400'
            )}
          >
            {lastCompilationResult.success ? (
              <CheckCircle className="w-3.5 h-3.5" />
            ) : (
              <AlertCircle className="w-3.5 h-3.5" />
            )}
            <span>
              {lastCompilationResult.success
                ? `Build succeeded (${lastCompilationResult.duration}ms)`
                : 'Build failed'}
            </span>
          </div>
        ) : null}

        {/* Diagnostics summary */}
        <button
          onClick={handleDiagnosticsClick}
          className="flex items-center gap-3 hover:bg-zinc-800 px-2 py-0.5 rounded transition-colors"
          title="View Problems"
        >
          {/* Errors */}
          <div
            className={cn(
              'flex items-center gap-1',
              diagnosticCounts.errors > 0 ? 'text-red-400' : 'text-zinc-500'
            )}
          >
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{diagnosticCounts.errors}</span>
          </div>

          {/* Warnings */}
          <div
            className={cn(
              'flex items-center gap-1',
              diagnosticCounts.warnings > 0 ? 'text-yellow-400' : 'text-zinc-500'
            )}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{diagnosticCounts.warnings}</span>
          </div>

          {/* Info */}
          <div
            className={cn(
              'flex items-center gap-1',
              diagnosticCounts.infos > 0 ? 'text-blue-400' : 'text-zinc-500'
            )}
          >
            <Info className="w-3.5 h-3.5" />
            <span>{diagnosticCounts.infos}</span>
          </div>
        </button>
      </div>
    </div>
  );
};
